import endpoints from "@/api/endpoints";
import httpClient from "@/api/http-client";
import useOnClickOutside from "@/hooks/useOnClickOutside";
import { selectCategory } from "@/store/slices/category";
import { combineCategoryFilters } from "@/utils/combineCategoryFilters";
import { resolveCategorySlug } from "@/utils/resolveCategorySlug";
import { useRouter } from "next/router";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

const priceFilters = [
  { label: "Below ₹ 10,000", value: { max: 10000 } },
  { label: "₹ 10,000 - ₹ 20,000", value: { min: 10000, max: 20000 } },
  { label: "₹ 20,000 - ₹ 30,000", value: { min: 20000, max: 30000 } },
  { label: "₹ 30,000 - ₹ 40,000", value: { min: 30000, max: 40000 } },
  { label: "₹ 40,000 - ₹ 50,000", value: { min: 40000, max: 50000 } },
  { label: "₹ 50,000 and Above", value: { min: 50000 } },
];

const CheckIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M20 6L9 17L4 12"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const DesktopFilterDropdown = ({ isSearch }: any) => {
  const router = useRouter();
  const categoryState = useSelector(selectCategory);

  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => setOpen(false));

  const [openAccordions, setOpenAccordions] = useState([
    "categories",
    "price",
    "metal",
    "occasion",
  ]);

  const toggleAccordion = (name: string) => {
    if (openAccordions.includes(name)) {
      setOpenAccordions(openAccordions.filter((a) => a !== name));
    } else {
      setOpenAccordions([...openAccordions, name]);
    }
  };

  // Data processing
  const categoryFacet = categoryState?.facets?.multilevel?.list?.find(
    (x: any) => x.filterField == "categoryPath"
  );
  const categoryDisplayName = categoryFacet?.displayName || "Categories";
  const categoryValues = categoryFacet?.values || [];
  const categoryItems = categoryValues.map((v: any) => ({
    label: `${v.name} (${v.count})`,
    value: v.name,
  }));

  const metalArray = categoryState?.facets?.text?.list?.find(
    (x: any) => x.facetName == "vPurity_uFilter"
  )?.values;
  const metalItems =
    metalArray?.reduce((acc: any, cur: any, index: any) => {
      if (index % 2 === 0) {
        acc.push({ label: `${cur} (${metalArray[index + 1]})`, value: cur });
      }
      return acc;
    }, []) || [];

  const occasionArray = categoryState?.facets?.text?.list?.find(
    (x: any) => x.facetName == "vOccasions_uFilter"
  )?.values;
  const occasionItems =
    occasionArray?.reduce((acc: any, cur: any, index: any) => {
      if (index % 2 === 0) {
        acc.push({ label: `${cur} (${occasionArray[index + 1]})`, value: cur });
      }
      return acc;
    }, []) || [];

  // Active filters mapping
  const activeCategory = !isSearch ? categoryState?.data?.name : null;

  let activePriceLabel: string | null = null;
  const qPriceMin = router.query.priceMin
    ? Number(router.query.priceMin)
    : undefined;
  const qPriceMax = router.query.priceMax
    ? Number(router.query.priceMax)
    : undefined;

  const activePriceObj = priceFilters.find(
    (p) => p.value.min === qPriceMin && p.value.max === qPriceMax
  );
  if (activePriceObj) {
    activePriceLabel = activePriceObj.label;
  }

  let activeMetals: string[] = [];
  if (router.query.metal) {
    activeMetals =
      typeof router.query.metal === "string"
        ? [router.query.metal]
        : (router.query.metal as string[]);
  }

  let activeOccasions: string[] = [];
  if (router.query.occasion) {
    activeOccasions =
      typeof router.query.occasion === "string"
        ? [router.query.occasion]
        : (router.query.occasion as string[]);
  }

  // Action handlers
  const handleCategoryChange = async (catName: string) => {
    // If selecting "All Products", we go to search
    if (!catName) {
      router.push({ pathname: "/jewellery" });
      return;
    }
    try {
      const slug = await resolveCategorySlug(
        catName,
        isSearch ? undefined : categoryState.data?.id
      );
      if (slug) {
        router.push({ pathname: `/jewellery/category/${slug}` });
      }
    } catch (error) {}
  };

  const clearCategory = () => {
    router.push({ pathname: "/jewellery" });
  };

  const handlePriceChange = (value: { min?: number; max?: number }) => {
    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({
              ...router.query,
              priceMin: value.min,
              priceMax: value.max,
            }),
            q: router.query.q,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          pathname: "/jewellery/category/[category]",
          query: {
            category: categoryState.data?.slug,
            ...combineCategoryFilters({
              ...router.query,
              priceMin: value.min,
              priceMax: value.max,
            }),
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const clearPrice = () => {
    handlePriceChange({ min: undefined, max: undefined });
  };

  const handleMetalChange = (metalVal: string) => {
    let newMetals = [...activeMetals];
    if (newMetals.includes(metalVal)) {
      newMetals = newMetals.filter((m) => m !== metalVal);
    } else {
      newMetals.push(metalVal);
    }

    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({ ...router.query, metal: newMetals }),
            q: router.query.q,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          pathname: "/jewellery/category/[category]",
          query: {
            category: categoryState.data?.slug,
            ...combineCategoryFilters({ ...router.query, metal: newMetals }),
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const clearMetal = (metalVal: string) => {
    handleMetalChange(metalVal);
  };

  const handleOccasionChange = (occasionVal: string) => {
    let newOccasions = [...activeOccasions];
    if (newOccasions.includes(occasionVal)) {
      newOccasions = newOccasions.filter((o) => o !== occasionVal);
    } else {
      newOccasions.push(occasionVal);
    }

    if (isSearch) {
      router.replace(
        {
          pathname: "/jewellery",
          query: {
            ...combineCategoryFilters({ ...router.query, occasion: newOccasions }),
            q: router.query.q,
          },
        },
        undefined,
        { shallow: true }
      );
    } else {
      router.replace(
        {
          pathname: "/jewellery/category/[category]",
          query: {
            category: categoryState.data?.slug,
            ...combineCategoryFilters({ ...router.query, occasion: newOccasions }),
          },
        },
        undefined,
        { shallow: true }
      );
    }
  };

  const clearOccasion = (occasionVal: string) => {
    handleOccasionChange(occasionVal);
  };

  const clearAll = () => {
    router.push({ pathname: "/jewellery" });
  };

  const hasAnyFilter =
    activeCategory || activePriceLabel || activeMetals.length > 0 || activeOccasions.length > 0;

  return (
    <div
      className="d-flex align-items-center flex-wrap gap-3"
      style={{ position: "relative" }}
      ref={ref}
    >
      {/* Trigger Button */}
      <div style={{ position: "relative" }}>
        <button
          onClick={() => setOpen(!open)}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            background: "#fff",
            border: "1px solid #ddd",
            padding: "8px 16px",
            borderRadius: "6px",
            cursor: "pointer",
            fontWeight: "500",
          }}
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M22 3H2L10 12.46V19L14 21V12.46L22 3Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          Filter By
          <svg
            width="12"
            height="12"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: open ? "rotate(180deg)" : "none",
              transition: "0.2s",
            }}
          >
            <path
              d="M6 9L12 15L18 9"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        {/* Dropdown Menu */}
        {open && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 8px)",
              left: 0,
              width: "300px",
              background: "#fff",
              boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
              borderRadius: "8px",
              zIndex: 100,
              maxHeight: "450px",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <div style={{ padding: "16px", overflowY: "auto", flex: 1 }}>
              {/* Categories Accordion */}
              {categoryItems.length > 0 && (
                <div style={{ marginBottom: "16px" }}>
                  <div
                    onClick={() => toggleAccordion("categories")}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      fontWeight: "600",
                      marginBottom: openAccordions.includes("categories")
                        ? "12px"
                        : "0",
                    }}
                  >
                    <span>
                      {categoryDisplayName} ({activeCategory ? 1 : 0})
                    </span>
                    <span>
                      {openAccordions.includes("categories") ? "▲" : "▼"}
                    </span>
                  </div>
                  {openAccordions.includes("categories") && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {categoryItems.map((cat: any) => (
                        <label
                          key={cat.value}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            margin: 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={activeCategory === cat.value}
                            onChange={() => handleCategoryChange(cat.value)}
                            style={{
                              accentColor: "#a31d24",
                              width: "16px",
                              height: "16px",
                            }}
                          />
                          <span
                            style={{
                              color:
                                activeCategory === cat.value
                                  ? "#a31d24"
                                  : "inherit",
                              fontWeight:
                                activeCategory === cat.value ? "500" : "400",
                            }}
                          >
                            {cat.label}
                          </span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Price Accordion */}
              <div
                style={{
                  marginBottom: "16px",
                  borderTop: "1px solid #f0f0f0",
                  paddingTop: "16px",
                }}
              >
                <div
                  onClick={() => toggleAccordion("price")}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    cursor: "pointer",
                    fontWeight: "600",
                    marginBottom: openAccordions.includes("price")
                      ? "12px"
                      : "0",
                  }}
                >
                  <span>Price ({activePriceLabel ? 1 : 0})</span>
                  <span>{openAccordions.includes("price") ? "▲" : "▼"}</span>
                </div>
                {openAccordions.includes("price") && (
                  <div
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "10px",
                    }}
                  >
                    {priceFilters.map((p) => {
                      const isChecked = activePriceLabel === p.label;
                      return (
                        <label
                          key={p.label}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            cursor: "pointer",
                            margin: 0,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => {
                              if (isChecked) clearPrice();
                              else handlePriceChange(p.value);
                            }}
                            style={{
                              accentColor: "#a31d24",
                              width: "16px",
                              height: "16px",
                            }}
                          />
                          <span
                            style={{
                              color: isChecked ? "#a31d24" : "inherit",
                              fontWeight: isChecked ? "500" : "400",
                            }}
                          >
                            {p.label}
                          </span>
                        </label>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Metal Accordion */}
              {metalItems.length > 0 && (
                <div
                  style={{
                    marginBottom: "16px",
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: "16px",
                  }}
                >
                  <div
                    onClick={() => toggleAccordion("metal")}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      fontWeight: "600",
                      marginBottom: openAccordions.includes("metal")
                        ? "12px"
                        : "0",
                    }}
                  >
                    <span>Metal ({activeMetals.length})</span>
                    <span>{openAccordions.includes("metal") ? "▲" : "▼"}</span>
                  </div>
                  {openAccordions.includes("metal") && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {metalItems.map((m: any) => {
                        const isChecked = activeMetals.includes(m.value);
                        return (
                          <label
                            key={m.value}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              cursor: "pointer",
                              margin: 0,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleMetalChange(m.value)}
                              style={{
                                accentColor: "#a31d24",
                                width: "16px",
                                height: "16px",
                              }}
                            />
                            <span
                              style={{
                                color: isChecked ? "#a31d24" : "inherit",
                                fontWeight: isChecked ? "500" : "400",
                              }}
                            >
                              {m.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}

              {/* Occasion Accordion */}
              {occasionItems.length > 0 && (
                <div
                  style={{
                    marginBottom: "16px",
                    borderTop: "1px solid #f0f0f0",
                    paddingTop: "16px",
                  }}
                >
                  <div
                    onClick={() => toggleAccordion("occasion")}
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      fontWeight: "600",
                      marginBottom: openAccordions.includes("occasion")
                        ? "12px"
                        : "0",
                    }}
                  >
                    <span>Occasion ({activeOccasions.length})</span>
                    <span>{openAccordions.includes("occasion") ? "▲" : "▼"}</span>
                  </div>
                  {openAccordions.includes("occasion") && (
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px",
                      }}
                    >
                      {occasionItems.map((m: any) => {
                        const isChecked = activeOccasions.includes(m.value);
                        return (
                          <label
                            key={m.value}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              cursor: "pointer",
                              margin: 0,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleOccasionChange(m.value)}
                              style={{
                                accentColor: "#a31d24",
                                width: "16px",
                                height: "16px",
                              }}
                            />
                            <span
                              style={{
                                color: isChecked ? "#a31d24" : "inherit",
                                fontWeight: isChecked ? "500" : "400",
                              }}
                            >
                              {m.label}
                            </span>
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
            {/* Footer */}
            <div
              style={{
                borderTop: "1px solid #f0f0f0",
                padding: "16px",
                textAlign: "center",
              }}
            >
              <button
                onClick={clearAll}
                style={{
                  color: "#a31d24",
                  background: "transparent",
                  border: "1px solid #a31d24",
                  borderRadius: "4px",
                  padding: "8px 24px",
                  width: "100%",
                  fontWeight: "600",
                }}
              >
                Clear All
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Active Filter Chips */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          flexWrap: "wrap",
          flex: 1,
        }}
      >
        <div
          style={{
            height: "24px",
            display: "inline-block",
            width: "1px",
            background: "#ddd",
            margin: "0 8px",
          }}
        ></div>
        {activeCategory && (
          <span
            style={{
              background: "#f5f5f5",
              padding: "9px 12px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
            }}
          >
            {activeCategory}
            <span
              onClick={clearCategory}
              style={{
                cursor: "pointer",
                opacity: 0.6,
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              ✕
            </span>
          </span>
        )}
        {activePriceLabel && (
          <span
            style={{
              background: "#f5f5f5",
              padding: "9px 12px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
            }}
          >
            {activePriceLabel}
            <span
              onClick={clearPrice}
              style={{
                cursor: "pointer",
                opacity: 0.6,
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              ✕
            </span>
          </span>
        )}
        {activeMetals.map((metal) => (
          <span
            key={metal}
            style={{
              background: "#f5f5f5",
              padding: "9px 12px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
            }}
          >
            {metal}
            <span
              onClick={() => clearMetal(metal)}
              style={{
                cursor: "pointer",
                opacity: 0.6,
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              ✕
            </span>
          </span>
        ))}
        {activeOccasions.map((occ) => (
          <span
            key={occ}
            style={{
              background: "#f5f5f5",
              padding: "9px 12px",
              borderRadius: "6px",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              fontSize: "14px",
            }}
          >
            {occ}
            <span
              onClick={() => clearOccasion(occ)}
              style={{
                cursor: "pointer",
                opacity: 0.6,
                fontSize: "12px",
                fontWeight: "bold",
              }}
            >
              ✕
            </span>
          </span>
        ))}
        {hasAnyFilter && (
          <span
            onClick={clearAll}
            style={{
              color: "#a31d24",
              cursor: "pointer",
              fontSize: "14px",
              fontWeight: "500",
              marginLeft: "8px",
            }}
          >
            Clear All
          </span>
        )}
      </div>
    </div>
  );
};

export default DesktopFilterDropdown;
